import { useState } from "react";
import { useMutation } from "@apollo/client";
import { Link, useNavigate } from "react-router";
import { useToast } from "../../../components/ui/Toast";
import { CREATE_PRICING_PLAN, ALL_PRICING_PLANS } from "./queries";
import type { PricingPlanRecord } from "./types";

export function PricingPlanCreate() {
  const navigate = useNavigate();
  const toast = useToast();

  const [createPlan, { loading: saving }] = useMutation(CREATE_PRICING_PLAN);

  const [name, setName] = useState("");
  const [authAmount, setAuthAmount] = useState<number>(0);
  const [hourlyRate, setHourlyRate] = useState<number>(0);

  const canSave = name.trim().length > 0 && !saving;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSave) return;

    try {
      const result = await createPlan({
        variables: {
          name: name.trim(),
          authAmount,
          hourlyRate,
        },
        refetchQueries: [{ query: ALL_PRICING_PLANS }],
      });
      const created = result.data?.createPricingPlan as
        | PricingPlanRecord
        | undefined;
      if (created) {
        toast.push({
          kind: "success",
          title: `${created.name} created`,
          description: "The new pricing plan is ready.",
        });
        navigate(`/settings/catalog/plans/${created.id}`);
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Couldn't create plan",
        description: msg,
      });
    }
  }

  return (
    <div className="max-w-xl">
      <form onSubmit={handleSubmit} className="px-7 py-8 space-y-6">
        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-1">
            New plan
          </div>
          <h2 className="font-display text-[26px] font-medium tracking-tight">
            Create a price plan
          </h2>
          <p className="text-[12.5px] text-ink-3 mt-1.5">
            Plans are reusable. After creating one, attach it to services
            through their pricing tier configuration.
          </p>
        </div>

        <div>
          <div className="text-[11.5px] text-tide font-medium uppercase tracking-wider mb-3">
            Plan details
          </div>
          <div className="bg-paper-hi rounded-lg shadow-card p-5 space-y-4">
            <label className="block">
              <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                Name *
              </span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Standard"
                autoFocus
                className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] focus:outline-none focus:ring-2 focus:ring-tide/40"
              />
            </label>

            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Authorization amount ($)
                </span>
                <input
                  type="number"
                  value={authAmount}
                  onChange={(e) =>
                    setAuthAmount(parseFloat(e.target.value) || 0)
                  }
                  min={0}
                  step={0.01}
                  placeholder="250"
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>

              <label className="block">
                <span className="text-[11px] text-ink-4 uppercase tracking-wider font-semibold">
                  Hourly rate ($)
                </span>
                <input
                  type="number"
                  value={hourlyRate}
                  onChange={(e) =>
                    setHourlyRate(parseFloat(e.target.value) || 0)
                  }
                  min={0}
                  step={0.01}
                  placeholder="400"
                  className="w-full mt-1 px-3 py-2 rounded-md hair border bg-canvas/30 text-[13px] num focus:outline-none focus:ring-2 focus:ring-tide/40"
                />
              </label>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="hair-t border-t pt-5 flex items-center justify-end gap-2">
          <Link
            to="/settings/catalog"
            className="px-3 py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 hover:bg-paper-2"
          >
            Discard
          </Link>
          <button
            type="submit"
            disabled={!canSave}
            className="px-3.5 py-1.5 rounded-md bg-tide text-paper-hi text-[12.5px] font-medium hover:bg-tide-d disabled:bg-ink-6 disabled:cursor-not-allowed"
          >
            {saving ? "Creating..." : "Create plan"}
          </button>
        </div>
      </form>
    </div>
  );
}
